import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

// Mock data types
interface MockUser {
  id: string;
  user_id: string;
  totalDrinks: number;
  longestStreak: number;
  beer: number;
  wine: number;
  shot: number;
}

interface DemoContextType {
  isDemo: boolean;
  demoTotalDrinks: number;
  logDemoDrink: (type: 'beer' | 'wine' | 'shot') => void;
  mockUsers: MockUser[];
  mockGroup: {
    invite_code: string;
    challenge_end: string;
    challenge_start: string;
    status: 'active';
  };
  mockProfile: {
    user_id: string;
  };
  demoLongestStreak: number;
  demoCurrentStreak: number;
  demoBeerCount: number;
  demoWineCount: number;
  demoShotCount: number;
}

const DemoContext = createContext<DemoContextType | undefined>(undefined);

// Mock leaderboard users with realistic Slovak names
const MOCK_USERS: MockUser[] = [
  { id: '1', user_id: 'marek_k', totalDrinks: 12, longestStreak: 45, beer: 8, wine: 3, shot: 1 },
  { id: '2', user_id: 'zuzka99', totalDrinks: 18, longestStreak: 28, beer: 10, wine: 5, shot: 3 },
  { id: '3', user_id: 'peter_m', totalDrinks: 24, longestStreak: 21, beer: 15, wine: 6, shot: 3 },
  { id: '4', user_id: 'katka_b', totalDrinks: 31, longestStreak: 14, beer: 12, wine: 14, shot: 5 },
  { id: '5', user_id: 'juraj_s', totalDrinks: 45, longestStreak: 10, beer: 25, wine: 12, shot: 8 },
  { id: '6', user_id: 'simona_h', totalDrinks: 52, longestStreak: 7, beer: 20, wine: 22, shot: 10 },
  // Over-limit user for Section B
  { id: '7', user_id: 'tomas_r', totalDrinks: 112, longestStreak: 3, beer: 65, wine: 30, shot: 17 },
];

// Demo user starts with 3 drinks to show some progress
const DEMO_USER: MockUser = {
  id: 'demo',
  user_id: 'demo_user',
  totalDrinks: 3,
  longestStreak: 10, // Unlocks first 2 milestones (7 + partial on 14)
  beer: 2,
  wine: 1,
  shot: 0,
};

export function DemoProvider({ children }: { children: ReactNode }) {
  const [demoUser, setDemoUser] = useState<MockUser>(DEMO_USER);

  // Calculate challenge dates (started 30 days ago, ends in 335 days)
  const challengeStart = new Date();
  challengeStart.setDate(challengeStart.getDate() - 30);
  
  const challengeEnd = new Date();
  challengeEnd.setDate(challengeEnd.getDate() + 335);

  const mockGroup = {
    invite_code: 'DEMO99',
    challenge_start: challengeStart.toISOString(),
    challenge_end: challengeEnd.toISOString(),
    status: 'active' as const,
  };

  const mockProfile = {
    user_id: 'demo_user',
  };

  const logDemoDrink = useCallback((type: 'beer' | 'wine' | 'shot') => {
    setDemoUser(prev => ({
      ...prev,
      totalDrinks: prev.totalDrinks + 1,
      [type]: prev[type] + 1,
    }));
  }, []);

  // Combine demo user with mock users for leaderboard
  const mockUsers = [
    { ...demoUser, user_id: 'ty (demo)' },
    ...MOCK_USERS,
  ].sort((a, b) => {
    if (a.totalDrinks !== b.totalDrinks) return a.totalDrinks - b.totalDrinks;
    return b.longestStreak - a.longestStreak;
  });

  return (
    <DemoContext.Provider
      value={{
        isDemo: true,
        demoTotalDrinks: demoUser.totalDrinks,
        logDemoDrink,
        mockUsers,
        mockGroup,
        mockProfile,
        demoLongestStreak: demoUser.longestStreak,
        demoCurrentStreak: 8, // Current streak for progress bar
        demoBeerCount: demoUser.beer,
        demoWineCount: demoUser.wine,
        demoShotCount: demoUser.shot,
      }}
    >
      {children}
    </DemoContext.Provider>
  );
}

export function useDemo() {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error('useDemo must be used within a DemoProvider');
  }
  return context;
}

// Hook to check if we're in demo mode without throwing
export function useMaybeDemo() {
  return useContext(DemoContext);
}
